import React, { Suspense, lazy } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../Context/AuthContext";
import LoadingSpinner from "../components/LoadingSpinner/LoadingSpinner";

const HomePage = lazy(() => import("../Pages/HomePage"));
const Login = lazy(() => import("../Pages/Auth/Login/Login"));
const Invite = lazy(() => import("../Pages/Auth/Invite/Invite"));
const SignUp = lazy(() => import("../Pages/Auth/SignUp/SignUp"));
const ClientInvite = lazy(() => import("../Pages/Auth/ClientInvite/ClientInvite"));
const Dashboard = lazy(() => import("../Pages/Dashboard/Index"));
const About = lazy(() => import("../Pages/About"));
const Contact = lazy(() => import("../Pages/Contact"));
const Terms = lazy(() => import("../Pages/Terms"));
const Privacy = lazy(() => import("../Pages/Privacy"));
const Cookies = lazy(() => import("../Pages/Cookies"));
const CheckoutPage = lazy(() => import("../checkout/CheckoutPage"));
const Success = lazy(() => import("../checkout/Success"));
const Fail = lazy(() => import("../checkout/Fail"));
const TestStripe = lazy(() => import("../Pages/TestStripe"));
const SharedProject = lazy(() => import("../Pages/Public/SharedProject"));
const SharedDocument = lazy(() => import("../Pages/Public/SharedDocument"));
const OAuthConsent = lazy(() => import("../Pages/OAuth/OAuthConsent"));

const LoadingFallback = () => <LoadingSpinner fullScreen />;

// Protected route component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingFallback />;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

const AppRoutes = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingFallback />;
  }

  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        <Route path="/" element={<HomePage />} />

        {/* Public pages */}
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/cookies" element={<Cookies />} />

        {/* Public shared project view */}
        <Route path="/shared/project/:token" element={<SharedProject />} />
        <Route path="/shared/document/:token" element={<SharedDocument />} />

        {/* OAuth consent screen */}
        <Route path="/oauth/authorize" element={<OAuthConsent />} />

        {/* Test Stripe */}
        <Route path="/test-stripe" element={<TestStripe />} />

        {/* Checkout public routes */}
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/checkout/success" element={<Success />} />
        <Route path="/checkout/fail" element={<Fail />} />

        {/* Login route - redirect to dashboard if already logged in */}
        <Route
          path="/login"
          element={user ? <Navigate to="/dashboard" replace /> : <Login />}
        />

        {/* Invite and Signup routes (public) */}
        <Route path="/invite" element={<Invite />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/client-invite" element={<ClientInvite />} />

        {/* Dashboard routes */}
        <Route
          path="/dashboard/*"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />

        {/* Fallback route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
